import { useState, useEffect, useMemo, startTransition } from 'react';
import type { HTMLAttributes } from 'react';
import { Global, useTheme } from '@emotion/react';
import { DayPicker } from 'react-day-picker';
import type {
  DateRange,
  Matcher,
  Modifiers,
  PropsBase,
  PropsRange,
} from 'react-day-picker';
import { format } from 'date-fns/format';
import { isBefore } from 'date-fns/isBefore';
import { isSameDay } from 'date-fns/isSameDay';
import type { Locale } from 'date-fns';
import { useTranslations } from 'next-intl';

import Close from '@engineering/icons/close';
import { CustomMonths, CustomMonthsContext } from './CustomMonths';
import {
  CustomNextMonthButton,
  CustomPreviousMonthButton,
} from './CustomMonthButtons';

import {
  GlobalStyles,
  Wrapper,
  StyledHeader,
  StyledFooter,
  Arrow,
  ConfirmationButton,
  CloseButton,
  StyledHintText,
  StyledSummaryText,
} from './DatePicker.styles';

const HOUR_IN_MS = 1000 * 60 * 60;

type Props = {
  locale: Locale;
  dayCount?: number;
  closeText?: string;
  dateFormat: string;
  scrollToSelectedMonth: boolean;
  vertical: boolean;
  showHeader: boolean;
  showFooter: boolean;
  onDatesChange?: (dateRange: DateRange, shouldClose?: boolean) => void;
  onClose?: () => void;
  onScrollToBottom?: () => void;
} & DateRange &
  PropsBase &
  PropsRange &
  Pick<HTMLAttributes<HTMLElement>, 'className'>;

const DatePicker = ({
  from,
  to,
  dayCount = 0,
  closeText,
  scrollToSelectedMonth = false,
  locale,
  dateFormat,
  vertical,
  showHeader,
  showFooter,
  className,
  onDatesChange,
  onScrollToBottom,
  onClose,
  ...props
}: Props) => {
  const t = useTranslations('entities.datePicker');
  const theme = useTheme();

  const [selectedFrom, setSelectedFrom] = useState(from || undefined);
  const [selectedTo, setSelectedTo] = useState(to || undefined);
  const [hoveredTo, setHoveredTo] = useState(undefined as undefined | Date);
  const [isWaitingForReturnDate, setIsWaitingForReturnDate] = useState(false);

  // Detect if we're in "waiting for return date" mode when picker opens
  useEffect(() => {
    if (from && to && isSameDay(from, to)) {
      setIsWaitingForReturnDate(true);
    } else {
      setIsWaitingForReturnDate(false);
    }
  }, [from, to]);

  const hintText = useMemo(() => {
    if (isWaitingForReturnDate || (selectedFrom && !selectedTo)) {
      return t.raw('datePicker.hintTextDropoff');
    }
    return t.raw('datePicker.hintTextPickup');
  }, [isWaitingForReturnDate, selectedFrom, selectedTo, t]);

  useEffect(() => {
    if (!scrollToSelectedMonth) {
      return;
    }
    const selectedDay = document.querySelector<HTMLElement>('.rdp-selected');
    const selectedMonth = selectedDay?.closest<HTMLElement>('.rdp-month');
    if (selectedMonth) {
      selectedMonth.scrollIntoView({ block: 'center' });
    }
  }, [scrollToSelectedMonth]);

  const dayCountText = t('dayCount', { days: dayCount });

  const getHoveredDayCount = () => {
    // Either date may be unset; NaN then falls through to the guard below.
    const days =
      ((hoveredTo?.getTime() ?? NaN) - (selectedFrom?.getTime() ?? NaN)) /
      HOUR_IN_MS /
      24;
    if (Number.isNaN(days)) return 1;
    return Number.isInteger(days) ? days : Math.ceil(days);
  };

  const summaryText = t('datePicker.dayCount', {
    days: isWaitingForReturnDate
      ? getHoveredDayCount()
      : Math.max(dayCount || 1, 1),
  });

  const getToDateText = () => {
    if (isWaitingForReturnDate && hoveredTo)
      return format(hoveredTo, dateFormat, { locale });
    if (to) return format(to, dateFormat, { locale });
    return '---';
  };

  const isSelectingFirstDay = (date: Date) => {
    const isBeforeFirstDay = selectedFrom && isBefore(date, selectedFrom);
    const isRangeSelected =
      selectedFrom && selectedTo && !isSameDay(selectedFrom, selectedTo);
    return !selectedFrom || isBeforeFirstDay || isRangeSelected;
  };

  const getHighlightedDays = () => {
    if (isWaitingForReturnDate && hoveredTo) {
      return { from: selectedFrom ?? undefined, to: hoveredTo ?? undefined };
    }

    return { from: selectedFrom ?? undefined, to: selectedTo ?? undefined };
  };

  const getModifiers = () => {
    let modifiers: Record<string, Matcher | Matcher[]> = {
      sunday: { dayOfWeek: [0] }, // Highlight Sundays
    };
    if (selectedFrom) {
      modifiers = { ...modifiers, from: [selectedFrom] };
    }
    if (hoveredTo) {
      modifiers = { ...modifiers, to: hoveredTo };
    } else if (selectedTo) {
      modifiers = { ...modifiers, to: selectedTo };
    }
    return modifiers;
  };

  const handleDayClick = (date: Date, modifiers: Modifiers) => {
    if (modifiers.disabled) {
      return;
    }

    if (isSelectingFirstDay(date)) {
      startTransition(() => {
        setSelectedFrom(date);
        setSelectedTo(date);
        setHoveredTo(undefined);
        setIsWaitingForReturnDate(true);
        onDatesChange?.({ from: date, to: date }, false);
      });
    } else {
      startTransition(() => {
        setSelectedTo(date);
        setHoveredTo(date);
        setIsWaitingForReturnDate(false);
        onDatesChange?.({ from: selectedFrom, to: date }, true);
      });
    }
  };

  const handleDayMouseEnter = (date: Date) => {
    if ((from && isBefore(date, from)) || isSelectingFirstDay(date)) {
      return;
    }
    setHoveredTo(date);
  };

  // Workaround for `react-day-picker` `onSelect` bug:
  // Apparently `onSelect` cannot be `undefined` or `selected` does not work properly.
  // @see https://github.com/gpbl/react-day-picker/issues/2362
  const handleSelect = () => undefined;

  const context = useMemo(
    () => ({ onScrollToBottom, vertical }),
    [onScrollToBottom, vertical],
  );

  const globalStyles = useMemo(
    () =>
      GlobalStyles({
        theme,
        vertical,
        isSelectingRange: isWaitingForReturnDate,
      }),
    [theme, vertical, isWaitingForReturnDate],
  );

  return (
    <CustomMonthsContext.Provider value={context}>
      <Global styles={globalStyles} />
      <Wrapper
        open
        className={className}
        hasHeader={showHeader}
        hasFooter={showFooter}
      >
        {showHeader && (
          <StyledHeader>
            <time>{from ? format(from, dateFormat, { locale }) : '---'}</time>
            <Arrow />
            <time>{to ? format(to, dateFormat, { locale }) : '---'}</time>
            <CloseButton icon={Close} title={closeText} onClick={onClose} />
          </StyledHeader>
        )}
        {!vertical && (
          <StyledHintText
            dangerouslySetInnerHTML={{
              __html: hintText,
            }}
          />
        )}
        <DayPicker
          components={{
            Months: CustomMonths,
            NextMonthButton: CustomNextMonthButton,
            PreviousMonthButton: CustomPreviousMonthButton,
          }}
          disableNavigation={vertical}
          onDayClick={handleDayClick}
          onDayMouseEnter={handleDayMouseEnter}
          onSelect={handleSelect}
          selected={getHighlightedDays()}
          modifiers={getModifiers()}
          modifiersClassNames={{
            from: 'rdp-day_from',
            to: 'rdp-day_to',
            sunday: 'rdp-day_sunday',
          }}
          disabled={{ before: new Date() }}
          locale={locale}
          {...props}
          mode="range"
        />
        {summaryText && !vertical && (
          <StyledSummaryText>
            {from ? format(from, dateFormat, { locale }) : '---'} -{' '}
            {getToDateText()} ({summaryText})
          </StyledSummaryText>
        )}
        {showFooter && vertical && dayCountText && (
          <StyledFooter vertical={vertical}>
            <ConfirmationButton size="small" fullWidth onClick={onClose}>
              {dayCountText}
            </ConfirmationButton>
          </StyledFooter>
        )}
      </Wrapper>
    </CustomMonthsContext.Provider>
  );
};

export { DatePicker };
